import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Heading, Select, Text } from "@telia/teddy";
import {
    TELIA_PLAY_SEARCH_ADDRESSES,
    TELIA_PLAY_GET_VAID_FROM_ADDRESS,
    TELIA_PLAY_SELECT_MOCK_ADDRESS,
} from "../../state/actionTypes";
import { Container, ModalDialog, ProgressBar } from "../../../components";
import { useSelector, useDispatch } from "react-redux";
import * as LOADING_SELECTORS from "../../../loading/state/selectors";
import * as CUSTOMER_INFO_SELECTORS from "../../../checkout/state/selectors/customerInfoSelectors";
import { RootState } from "../../../../../models";
import {
    getAvailableAddresses,
    getFormById,
    getMockAddress,
    isBroadbandCustomer as isBroadbandCustomerSelector,
} from "../../state/selectors";
import { isBankIdVerified as isBankIdVerifiedSelector } from "../../../authentication/state/selectors";
import BankIdSection from "../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import {
    clearPackageUnavailable,
    searchServiceQualificationAddresses,
    saveContactInfo,
    saveContactInfoWithMockVaid,
    setCustomerSubmissionPending,
    clearMockAddress,
    selectMockAddress,
} from "../../state/actions";
import {
    formatMockAddress,
    formatMockPackage,
    TELIA_PLAY_MOCK_ADDRESSES,
    TELIA_PLAY_MOCK_PACKAGES,
} from "../../../../../shared/teliaPlayMockAddresses";
import TEXT from "../../text-consts";
import { resetOrder } from "../../../checkout/state/actions/phoneNumberActions";
import ContactCard from "../../../checkout/components/molecules/ContactCard/ContactCard";
import PAGES from "../../../../../constants/pages";
import PersonalDetailsForm from "../../components/organisms/PersonalDetailsForm/PersonalDetailsForm";
import { navigate } from "../../../navigation/state/actions";
import { allValidationRules, validateField, VALIDATION_FIELD } from "../../../rapidform/formValidation";
import { FORM_ID } from "../../../../../constants";
import { getPackageSlugFromId } from "../../../../../shared/teliaPlayPackageMapping";

import "./Customer.less";

const NO_MOCK_VALUE = "none";

type MockFetchStatus = "idle" | "loading" | "success" | "error";

const isValidContactInfo = (details?: Record<string, string>): boolean => {
    const email = details?.[VALIDATION_FIELD.PRODUCT_USER_EMAIL] ?? "";
    const phone = details?.[VALIDATION_FIELD.PRODUCT_USER_PHONE] ?? "";
    return (
        email.trim() !== "" &&
        validateField(email, allValidationRules[VALIDATION_FIELD.PRODUCT_USER_EMAIL]) === null &&
        validateField(phone, allValidationRules[VALIDATION_FIELD.PRODUCT_USER_PHONE]) === null
    );
};

const Customer: React.FC = () => {
    const dispatch = useDispatch();
    const isAtEnvironment = useSelector((state: RootState) => state.config?.environment === "at");
    const mockAddress = useSelector((state: RootState) => getMockAddress(state));
    const hasValidContactInfo = useSelector((state: RootState) =>
        isValidContactInfo(getFormById(state, FORM_ID.PERSONAL_DETAILS_TELIA_PLAY)?.values?.details),
    );
    const selectedPackageName = useSelector((state: RootState) => state.teliaPlay?.selectedPackage?.name);
    const isBroadbandCustomer = useSelector((state: RootState) => isBroadbandCustomerSelector(state));
    const [mockFetchStatus, setMockFetchStatus] = useState<MockFetchStatus>("idle");
    const [selectedMockId, setSelectedMockId] = useState(mockAddress ? String(mockAddress.id) : NO_MOCK_VALUE);
    const [selectedMockPackageId, setSelectedMockPackageId] = useState(NO_MOCK_VALUE);
    const isSelectingMockAddress = useSelector((state: RootState) =>
        LOADING_SELECTORS.isTypeLoading(state, TELIA_PLAY_SELECT_MOCK_ADDRESS),
    );
    const isBankIdVerified = useSelector((state: RootState) => isBankIdVerifiedSelector(state));
    const isCustomerSubmissionPending = useSelector(
        (state: RootState) => state.teliaPlay?.isCustomerSubmissionPending === true,
    );
    const isFetchingAddresses = useSelector((state: RootState) =>
        LOADING_SELECTORS.isTypeLoading(state, TELIA_PLAY_SEARCH_ADDRESSES),
    );
    const isFetchingVaid = useSelector((state: RootState) =>
        LOADING_SELECTORS.isTypeLoading(state, TELIA_PLAY_GET_VAID_FROM_ADDRESS),
    );
    const { packageId: urlPackageSlug } = useParams();
    const routerNavigate = useNavigate();
    const requestedPackageId = useSelector((state: RootState) => state.teliaPlay?.requestedPackageId);
    // Captured once so clearing an AT mock package restores the package the customer arrived with
    const [originalPackageId] = useState(requestedPackageId === undefined ? undefined : String(requestedPackageId));
    const mockPackageId =
        isAtEnvironment && selectedMockPackageId !== NO_MOCK_VALUE ? selectedMockPackageId : undefined;
    const packageId = mockPackageId ?? originalPackageId;
    const packageSlug = getPackageSlugFromId(packageId);
    const legalOwnerFullName = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerFullName(state));
    const legalOwnerCity = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerFullCity(state));
    const legalOwnerAddress = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwnerAddress(state));
    const legalOwnerFloor = useSelector((state: RootState) =>
        CUSTOMER_INFO_SELECTORS.getLegalOwner(state).floor?.trim(),
    );
    const legalOwnerDoor = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.getLegalOwner(state).door?.trim());
    const availableAddresses = useSelector((state: RootState) => getAvailableAddresses(state));
    const [showAllOnFloor, setShowAllOnFloor] = useState(false);
    const [showAllAddresses, setShowAllAddresses] = useState(false);
    const allAddresses = availableAddresses ?? [];
    const addressesOnFloor = legalOwnerFloor
        ? allAddresses.filter((address) => address.apartment.slice(1, 3) === legalOwnerFloor)
        : allAddresses;
    const addressesOnFloorAndDoor =
        legalOwnerFloor && legalOwnerDoor
            ? addressesOnFloor.filter((address) => address.apartment === legalOwnerDoor)
            : addressesOnFloor;
    const hasExactMatch = addressesOnFloorAndDoor.length > 0 && addressesOnFloorAndDoor !== addressesOnFloor;
    const realAvailableAddressesOnFloor = showAllAddresses
        ? allAddresses
        : showAllOnFloor || !hasExactMatch
          ? addressesOnFloor
          : addressesOnFloorAndDoor;
    // A mock address only has a VAID, so no apartment can be selected
    const availableAddressesOnFloor = mockAddress ? [] : realAvailableAddressesOnFloor;
    const isApartmentLocked = hasExactMatch && !showAllOnFloor && !showAllAddresses;
    const handleShowMoreAddresses = () => {
        if (hasExactMatch && !showAllOnFloor && addressesOnFloor.length > addressesOnFloorAndDoor.length) {
            setShowAllOnFloor(true);
        } else {
            setShowAllAddresses(true);
        }
    };
    const [selectedApartment, setSelectedApartment] = useState("");
    const selectedAddress =
        availableAddressesOnFloor.find((address) => address.apartment === selectedApartment) ??
        (availableAddressesOnFloor.length === 1 ? availableAddressesOnFloor[0] : undefined);
    const isSubmittingRef = useRef(false);
    const [showLogOutBankIdModal, setShowLogoutBankIdModal] = useState(false);

    useEffect(() => {
        if (!isBankIdVerified) {
            setSelectedMockId(NO_MOCK_VALUE);
            setSelectedMockPackageId(NO_MOCK_VALUE);
            setSelectedApartment("");
            setShowAllOnFloor(false);
            setShowAllAddresses(false);
            return;
        }
        if (!legalOwnerFloor || !legalOwnerDoor) {
            return;
        }
        const legalOwnerApartment = legalOwnerDoor;
        if (allAddresses.some((address) => address.apartment === legalOwnerApartment)) {
            setSelectedApartment(legalOwnerApartment);
        }
    }, [availableAddresses, isBankIdVerified, legalOwnerFloor, legalOwnerDoor]);

    useEffect(() => {
        // A package that is unavailable at one apartment may be available at another, so allow a new attempt
        dispatch(clearPackageUnavailable());
    }, [selectedApartment, mockAddress?.id, mockPackageId]);

    const canFetchPackage =
        isBankIdVerified &&
        !isSelectingMockAddress &&
        !!packageId &&
        (mockAddress !== null || selectedAddress !== undefined);

    const saveContactInfoAndFetchPackage = async (onSuccess?: () => void): Promise<boolean> => {
        if (isSubmittingRef.current || !canFetchPackage || !packageId) {
            return false;
        }
        isSubmittingRef.current = true;
        dispatch(setCustomerSubmissionPending(true));
        try {
            if (mockAddress) {
                await dispatch(saveContactInfoWithMockVaid(packageId));
            } else if (selectedAddress) {
                await dispatch(saveContactInfo(selectedAddress.addressId, selectedAddress.apartment, packageId));
            }
            onSuccess?.();
            return true;
        } catch {
            // The API error is already shown to the user by asyncErrorHandlingMiddleware
            return false;
        } finally {
            isSubmittingRef.current = false;
            dispatch(setCustomerSubmissionPending(false));
        }
    };

    useEffect(() => {
        if (!isAtEnvironment || (!mockAddress && !mockPackageId && urlPackageSlug === packageSlug)) {
            setMockFetchStatus("idle");
            return;
        }
        if (isSubmittingRef.current) {
            return;
        }
        if (!canFetchPackage || !hasValidContactInfo) {
            setMockFetchStatus("idle");
            return;
        }
        const fetchPackageForMockSelection = async () => {
            setMockFetchStatus("loading");
            const isSuccess = await saveContactInfoAndFetchPackage();
            setMockFetchStatus(isSuccess ? "success" : "error");
            if (isSuccess && packageSlug && urlPackageSlug !== packageSlug) {
                // Keep the URL in line with the selected package, otherwise a reload starts a new order
                routerNavigate(`${PAGES.TELIA_PLAY.CUSTOMER.URL}${packageSlug}/`, { replace: true });
            }
        };
        fetchPackageForMockSelection();
    }, [
        mockAddress?.id,
        mockPackageId,
        packageSlug,
        hasValidContactInfo,
        canFetchPackage,
        selectedAddress?.addressId,
        selectedAddress?.apartment,
        isSelectingMockAddress,
        urlPackageSlug,
    ]);

    useEffect(() => {
        const handleSearchAddresses = async () => {
            if (
                isBankIdVerified &&
                packageId &&
                !mockAddress &&
                !isSelectingMockAddress &&
                (!availableAddresses || availableAddresses.length === 0)
            ) {
                dispatch(searchServiceQualificationAddresses(packageId));
            }
        };

        handleSearchAddresses();
    }, [isBankIdVerified, mockAddress?.id, isSelectingMockAddress]);

    return (
        <Container className="telia-play-customer" size="small">
            {isBankIdVerified && isFetchingAddresses && (
                <div className="telia-play-customer__loading" role="status" aria-live="polite" aria-atomic="true">
                    <img
                        className="telia-play-customer__loading-image"
                        src="/shop/assets/images/Telia_Play_Dama.png"
                        alt=""
                        width={762}
                        height={520}
                    />
                    <div className="telia-play-customer__loading-text">
                        <Heading as="h2" variant="title-100">
                            {TEXT.ADDRESSES.LOADING_HEADING}
                        </Heading>
                        <Text as="p" variant="paragraph-100-bold">
                            {TEXT.ADDRESSES.LOADING_ADDRESSES}
                        </Text>
                        <ProgressBar isFetchingData={isFetchingAddresses} backgroundColor="white" />
                    </div>
                </div>
            )}
            {isBankIdVerified && !isFetchingAddresses && (
                <>
                    <ContactCard
                        headingText={TEXT.PERSONAL_DETAILS.HEADING_BANK_ID_VERIFIED}
                        name={legalOwnerFullName}
                        address={legalOwnerAddress}
                        city={legalOwnerCity}
                        resetBankId={(): void => setShowLogoutBankIdModal(true)}
                        showContactInfo={false}
                        showLogoutBankId={!isCustomerSubmissionPending}
                    />
                    <Container
                        className="beta-checkout__section"
                        size="small"
                        whiteBg
                        noPaddingTop
                        data-cy="contact-info-form"
                    >
                        <PersonalDetailsForm
                            onSubmitDetails={async () => {
                                await saveContactInfoAndFetchPackage(() =>
                                    dispatch(navigate(PAGES.TELIA_PLAY.SUMMARY.URL)),
                                );
                            }}
                            availableAddresses={availableAddressesOnFloor}
                            canShowAllAddresses={!mockAddress && availableAddressesOnFloor.length < allAddresses.length}
                            onShowAllAddresses={handleShowMoreAddresses}
                            isApartmentLocked={isApartmentLocked}
                            setSelectedApartment={setSelectedApartment}
                            selectedApartment={selectedApartment}
                        />
                    </Container>
                    {isFetchingVaid && (
                        <div className="telia-play-customer__loading">
                            <Text>{TEXT.ADDRESSES.LOADING_VAID}</Text>
                            <ProgressBar isFetchingData={isFetchingVaid} backgroundColor="white" />
                        </div>
                    )}
                </>
            )}
            <Container className="telia-play-personal-details" size="small" whiteBg>
                <BankIdSection referer="telia-play" typeId="co_bankid" />
            </Container>
            {isAtEnvironment && isBankIdVerified && (
                <Container className="telia-play-customer__mock-address" size="small" whiteBg>
                    <Heading as="h2" variant="title-100">
                        Testdata (kun AT)
                    </Heading>
                    <Text as="p">
                        Midlertidig: velg en testadresse med gyldig VAID i stedet for adressen fra BankID, og/eller en
                        annen pakke enn den i URL-en.
                    </Text>
                    <Select
                        maxHeight={300}
                        label="Velg testadresse (VAID)"
                        name="mockAddress"
                        disabled={isCustomerSubmissionPending || isSelectingMockAddress}
                        options={[NO_MOCK_VALUE, ...TELIA_PLAY_MOCK_ADDRESSES.map(({ id }) => String(id))]}
                        value={selectedMockId}
                        onValueChange={(value) => {
                            const mock = TELIA_PLAY_MOCK_ADDRESSES.find(({ id }) => String(id) === value);
                            setSelectedMockId(mock ? value : NO_MOCK_VALUE);
                            // A failed request is already shown to the user by asyncErrorHandlingMiddleware
                            Promise.resolve(dispatch(mock ? selectMockAddress(mock.id) : clearMockAddress())).catch(
                                () => setSelectedMockId(mockAddress ? String(mockAddress.id) : NO_MOCK_VALUE),
                            );
                            return value;
                        }}
                    >
                        <Select.Trigger>
                            <Select.Value placeholder="Bruk adressen fra BankID" />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Viewport>
                                <Select.Item value={NO_MOCK_VALUE}>Bruk adressen fra BankID</Select.Item>
                                {TELIA_PLAY_MOCK_ADDRESSES.map((address) => (
                                    <Select.Item key={address.id} value={String(address.id)}>
                                        {formatMockAddress(address)}
                                    </Select.Item>
                                ))}
                            </Select.Viewport>
                        </Select.Content>
                    </Select>
                    <Select
                        maxHeight={300}
                        label="Velg testpakke"
                        name="mockPackage"
                        disabled={isCustomerSubmissionPending}
                        options={[NO_MOCK_VALUE, ...TELIA_PLAY_MOCK_PACKAGES.map(({ id }) => String(id))]}
                        value={selectedMockPackageId}
                        onValueChange={(value) => {
                            const mockPackage = TELIA_PLAY_MOCK_PACKAGES.find(({ id }) => String(id) === value);
                            setSelectedMockPackageId(mockPackage ? value : NO_MOCK_VALUE);
                            return value;
                        }}
                    >
                        <Select.Trigger>
                            <Select.Value placeholder="Bruk pakken fra URL-en" />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Viewport>
                                <Select.Item value={NO_MOCK_VALUE}>Bruk pakken fra URL-en</Select.Item>
                                {TELIA_PLAY_MOCK_PACKAGES.map((mockPackage) => (
                                    <Select.Item key={mockPackage.id} value={String(mockPackage.id)}>
                                        {formatMockPackage(mockPackage)}
                                    </Select.Item>
                                ))}
                            </Select.Viewport>
                        </Select.Content>
                    </Select>
                    {isSelectingMockAddress && <Text as="p">Lagrer VAID for testadressen …</Text>}
                    {!isSelectingMockAddress && selectedMockId !== NO_MOCK_VALUE && !mockAddress && (
                        <Text as="p">Kunne ikke bruke testadressen. Se feilmeldingen over.</Text>
                    )}
                    {mockAddress && <Text as="p">{`Bruker VAID ${mockAddress.vaid} fra testadressen.`}</Text>}
                    {(mockAddress || mockPackageId) && !hasValidContactInfo && mockFetchStatus === "idle" && (
                        <Text as="p">Fyll inn gyldig e-post og telefonnummer, så hentes pakken automatisk.</Text>
                    )}
                    {mockFetchStatus === "loading" && <Text as="p">{`Henter pakke ${packageId} …`}</Text>}
                    {mockFetchStatus === "success" && (
                        <Text as="p">
                            {isBroadbandCustomer
                                ? "Adressen har allerede Telia Bredbånd – pakke hentes ikke."
                                : `Pakke hentet: ${selectedPackageName ?? ""}`}
                        </Text>
                    )}
                    {mockFetchStatus === "error" && <Text as="p">Kunne ikke hente pakken. Se feilmeldingen over.</Text>}
                </Container>
            )}
            {showLogOutBankIdModal && (
                <ModalDialog
                    className="contact-card-container__logout-bankid"
                    size="small"
                    submitText="Ja, logg meg ut"
                    closeText="Nei, bli på siden"
                    closeKind="secondary"
                    heading="Ønsker du å logge ut av BankID?"
                    name="log-out-bank-id-modal"
                    onSubmit={(): void => {
                        dispatch(resetOrder());
                        setShowLogoutBankIdModal(false);
                    }}
                    onClose={() => setShowLogoutBankIdModal(false)}
                />
            )}
        </Container>
    );
};

export default Customer;
